import { useState } from "react";
import {
  Card,
  Button,
  Badge,
  Modal,
  FormLayout,
  Select,
  TextField,
  Banner,
  TextContainer,
  Divider,
  ButtonGroup,
  BlockStack,
  InlineStack,
} from "@shopify/polaris";
import type { CampaignVariant, Game, RewardPolicy } from "../../api/campaigns";
import { GameConfigForm } from "../GameConfigForm/GameConfigForm";

interface Props {
  campaignId: string;
  variants: CampaignVariant[];
  games: Game[];
  rewardPolicies: RewardPolicy[];
  onAddVariant: (variant: {
    gameId: string;
    gameVersionId: string;
    rewardPolicyId: string;
    weight: number;
    config: any;
  }) => Promise<void>;
  onUpdateVariant: (
    variantId: string,
    updates: { weight?: number; config?: any; status?: "ACTIVE" | "DISABLED" }
  ) => Promise<void>;
  onDeleteVariant: (variantId: string) => Promise<void>;
}

export function VariantManager({
  variants,
  games,
  rewardPolicies,
  onAddVariant,
  onUpdateVariant,
  onDeleteVariant,
}: Props) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New variant form state
  const [selectedGameId, setSelectedGameId] = useState<string>("");
  const [selectedGameVersionId, setSelectedGameVersionId] = useState<string>("");
  const [selectedRewardPolicyId, setSelectedRewardPolicyId] = useState<string>("");
  const [variantWeight, setVariantWeight] = useState<number>(100);
  const [variantConfig, setVariantConfig] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedGame = games.find((g) => g.id === selectedGameId);
  const selectedGameVersion = selectedGame?.versions?.find(
    (v) => v.id === selectedGameVersionId
  );

  const handleOpenAddModal = () => {
    setSelectedGameId(games[0]?.id || "");
    setSelectedGameVersionId(games[0]?.versions?.[0]?.id || "");
    setSelectedRewardPolicyId(rewardPolicies[0]?.id || "");
    setVariantWeight(100);
    setVariantConfig({});
    setError(null);
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setError(null);
  };

  const handleGameChange = (gameId: string) => {
    setSelectedGameId(gameId);
    const game = games.find((g) => g.id === gameId);
    setSelectedGameVersionId(game?.versions?.[0]?.id || "");
    setVariantConfig({});
  };

  const handleGameVersionChange = (versionId: string) => {
    setSelectedGameVersionId(versionId);
    setVariantConfig({});
  };

  const handleAddVariant = async () => {
    if (!selectedGameId || !selectedGameVersionId || !selectedRewardPolicyId) {
      setError("Please select a game, version, and reward policy");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onAddVariant({
        gameId: selectedGameId,
        gameVersionId: selectedGameVersionId,
        rewardPolicyId: selectedRewardPolicyId,
        weight: variantWeight,
        config: variantConfig,
      });
      handleCloseAddModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add variant");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleVariantStatus = async (variant: CampaignVariant) => {
    const newStatus = variant.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    try {
      await onUpdateVariant(variant.id, { status: newStatus });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update variant status");
    }
  };

  const handleDeleteVariant = async (variantId: string) => {
    if (!confirm("Are you sure you want to delete this variant?")) {
      return;
    }

    try {
      await onDeleteVariant(variantId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete variant");
    }
  };

  const getStatusBadge = (status: "ACTIVE" | "DISABLED") => {
    return status === "ACTIVE" ? (
      <Badge tone="success">Active</Badge>
    ) : (
      <Badge>Disabled</Badge>
    );
  };

  return (
    <Card>
      <div style={{ padding: "1rem" }}>
        <p style={{ fontWeight: 600, marginBottom: "1rem" }}>Campaign Variants</p>
        <TextContainer>
          <p>
            Variants allow you to A/B test different games or reward policies. Each variant
            has a weight that determines how often it's shown.
          </p>
        </TextContainer>

        {error && (
          <div style={{ marginTop: "1rem" }}>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </div>
        )}

        <Divider />

        <div style={{ marginTop: "1.5rem" }}>
          {variants.length === 0 ? (
            <Banner>
              No variants added yet. Add at least one variant to activate this campaign.
            </Banner>
          ) : (
            <BlockStack gap="400">
              {variants.map((variant) => (
                <Card key={variant.id}>
                  <div style={{ padding: "1rem" }}>
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="200">
                      <InlineStack gap="200">
                        <strong>{variant.game?.name || "Unknown Game"}</strong>
                        <Badge>{variant.gameVersion?.version || "Unknown Version"}</Badge>
                        {getStatusBadge(variant.status)}
                      </InlineStack>
                      <p style={{ color: "#6d7175", fontSize: "0.875rem" }}>
                        Reward: {variant.rewardPolicy?.name || "Unknown Policy"}
                      </p>
                      <p style={{ color: "#6d7175", fontSize: "0.875rem" }}>
                        Weight: {variant.weight}
                      </p>
                    </BlockStack>
                    <ButtonGroup>
                      <Button
                        size="slim"
                        onClick={() => handleToggleVariantStatus(variant)}
                      >
                        {variant.status === "ACTIVE" ? "Disable" : "Enable"}
                      </Button>
                      <Button
                        size="slim"
 tone="critical"                         onClick={() => handleDeleteVariant(variant.id)}
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </InlineStack>
                  </div>
                </Card>
              ))}
            </BlockStack>
          )}
        </div>

        <div style={{ marginTop: "1.5rem" }}>
          <Button onClick={handleOpenAddModal} disabled={games.length === 0 || rewardPolicies.length === 0}>
            Add Variant
          </Button>
          {games.length === 0 && (
            <p style={{ marginTop: "0.5rem", color: "#bf0711", fontSize: "0.875rem" }}>
              No games available. Please seed the database with games first.
            </p>
          )}
          {rewardPolicies.length === 0 && (
            <p style={{ marginTop: "0.5rem", color: "#bf0711", fontSize: "0.875rem" }}>
              No reward policies found. Create a reward policy first.
            </p>
          )}
        </div>
      </div>

      <Modal
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        title="Add Campaign Variant"
        primaryAction={{
          content: "Add Variant",
          onAction: handleAddVariant,
          loading: isSubmitting,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: handleCloseAddModal,
          },
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          <FormLayout>
            <Select
              label="Game"
              value={selectedGameId}
              options={games.map((game) => ({ label: game.name, value: game.id }))}
              onChange={handleGameChange}
            />

            {selectedGame && selectedGame.versions && selectedGame.versions.length > 0 && (
              <Select
                label="Game Version"
                value={selectedGameVersionId}
                options={selectedGame.versions.map((version) => ({
                  label: `${version.version} - ${version.status}`,
                  value: version.id,
                }))}
                onChange={handleGameVersionChange}
              />
            )}

            <Select
              label="Reward Policy"
              value={selectedRewardPolicyId}
              options={rewardPolicies.map((policy) => ({
                label: policy.name,
                value: policy.id,
              }))}
              onChange={setSelectedRewardPolicyId}
            />

            <TextField
              label="Weight"
              type="number"
              value={variantWeight.toString()}
              onChange={(value) => setVariantWeight(parseInt(value) || 100)}
              helpText="Higher weight = shown more often"
              min={1}
              autoComplete="off"
            />
          </FormLayout>

          {selectedGameVersion && selectedGameVersion.configSchema && (
            <div style={{ marginTop: "1.5rem" }}>
              <GameConfigForm
                schema={selectedGameVersion.configSchema}
                initialConfig={variantConfig}
                onChange={setVariantConfig}
              />
            </div>
          )}
        </Modal.Section>
      </Modal>
    </Card>
  );
}
